import { Button, CircularProgress, Container, Stack } from "@mui/material";
import { Helmet } from "react-helmet-async";
import Input from "./input";
import HomePageEditorProvider, { useRafflePageEditorContext } from "./provider";
import { StyledRow, StyledTypography, StyledUploadBox } from "./styles.css";
import UploadMask from "./upload-mask";
import DescriptionDialog from "./description-dialog";
import { useServices } from "./hooks";
import { useToasterContext } from "@src/context/Toaster";
import { AlertType } from "@src/lib/types/toaster";
import { useEffect } from "react";
import Config from "@src/utils/api/config";
import { LoadingButton } from "@mui/lab";
import SuccessDialog from "./success";

export default function RafflePageEditor() {
	return (
		<HomePageEditorProvider>
		<>
			<Helmet>
				<title> Raffle Page Editor </title>
			</Helmet>
      <Component />
      <DescriptionDialog />
      <SuccessDialog />
		</>
		</HomePageEditorProvider>
	)
}

function Component() {
	const { uploadImage } = useServices();
	const { triggerOpen } = useToasterContext();
	const { state, error, onDispatch, setContainer, isLoading, isDirty, onSubmit } = useRafflePageEditorContext();

	const handleFileChange = (type: string) => (event: any) => {
		const file = event.target.files[0];

		if (file) {
			if (file.size > 3 * 1024 * 1024) {
				triggerOpen({
					type: AlertType.error,
					title: "Failed",
					message: "File size must be less than 3MB",
				});
				return;
			}

			const formData = new FormData();
			formData.append("file", file);
			formData.append("type", type);
			uploadImage.mutate(formData);
		}
	};

	useEffect(() => {
		if(uploadImage.data && uploadImage.isSuccess){
			onDispatch(uploadImage.data.type)(`${Config.APP_CDN_URL}/${uploadImage.data.result.filePath}`);   
		}
	},[uploadImage.isSuccess, uploadImage.data, onDispatch]); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<Container maxWidth="xl" >
			
			<Stack sx={{width: "100%"}}>
				<Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} width={"auto"}>
					<StyledTypography >Main Banner</StyledTypography>
					<LoadingButton
						onClick={onSubmit}
						loading={isLoading}
						disabled={!isDirty}
						sx={{ width: 145,
							height: "50px", borderRadius: 1,
							color: "white", bgcolor: isDirty ? "#8F17D9" : "#484848",
							alignSelf: "center",
							"&:hover": { bgcolor: isDirty ? "#7a12bb" : "#333" },
						}} >
						PUBLISH
					</LoadingButton>
				</Stack>
				<StyledUploadBox sx={[{mt: 4 }, error.image && {border: "1px dashed red"}]} >
					<input
						type="file"
						id="image"
						accept="image/*"
						style={{ display: 'none' }}
						onChange={handleFileChange("image")}
					/>
					{!state.image && <UploadMask notes="Recommended size: 1085x300" 
						notesSx={{fontSize: 9}}
						notes2Sx={{fontSize: 9}}
						notes2="(recommended file type eg. .jpg, .png and file size 3mb or less)"/>}
					{uploadImage.isPending && 
						<CircularProgress sx={{position: "absolute"}} />}
					{state.image && 
						<img 
							src={state.image} 
							alt="Selected" 
							style={{ maxWidth: '90%', height: '90%' }} />}
				</StyledUploadBox>
				<Button  onClick={() => {
					document.getElementById('image')?.click();
				}} sx={{ width: 230, 
					height: 40, borderRadius: 1, 
					color: "white", bgcolor: "#484848", 
					alignSelf: "center", marginTop: 3}} >UPLOAD IMAGE</Button>
				<Stack>
					<Input 
						disabled
						label="Draw Description"
						labelSx={{
							fontSize: "20px",
							marginBottom: 16
						}}
						sx={{mt: 4}}
						inputSx={{
							'& .MuiInputBase-root': {
								minHeight: '98px',
							},
							"& .MuiOutlinedInput-root": {
								alignItems: "flex-start",
								padding: "12px 14px",           // 👈 Better paragraph feel
							},
							"& .MuiOutlinedInput-input": {
								fontSize: "15px",
								lineHeight: "22px",
								padding: 0,                     // removes default tight padding
							},
							"& .MuiInputBase-root.Mui-disabled": {
								opacity: 1,
								color: "black",
							},
							"& .MuiInputBase-input.Mui-disabled": {
								WebkitTextFillColor: "black", // required for Safari
							},
						}}
						placeholder='Description...'
						error={error.description}
						value={state.description}
						onChange={onDispatch("description")} 
						/>
					<Button  onClick={() => setContainer("description")} sx={{ width: 230, 
						height: 40, borderRadius: 1, 
						color: "white", bgcolor: "#484848", 
						alignSelf: "center", marginTop: 3}} >
						EDIT DRAW DESCRIPTION
					</Button>
				</Stack>

				<Stack>
					<Input 
						disabled
						label="Prize Description"
						labelSx={{
							fontSize: "20px",
							marginBottom: 16
						}}
						sx={{mt: 4}}
						inputSx={{
							'& .MuiInputBase-root': {
								minHeight: '98px',
								color: "black"
							},
							"& .MuiOutlinedInput-root": {
								alignItems: "flex-start",
								padding: "12px 14px",           // 👈 Better paragraph feel
							},
							"& .MuiOutlinedInput-input": {
								fontSize: "15px",
								lineHeight: "22px",
								padding: 0,                     // removes default tight padding
							},
							"& .MuiInputBase-root.Mui-disabled": {
								opacity: 1,
								color: "black",
							},
							"& .MuiInputBase-input.Mui-disabled": {
								WebkitTextFillColor: "black", // required for Safari
							},
						}}
						placeholder='Description...'
						error={error.prize}
						value={state.prize}
						onChange={onDispatch("prize")} 
					/>
					<Button onClick={() => setContainer("prize")} sx={{ width: 230, 
						height: 40, borderRadius: 1, 
						color: "white", bgcolor: "#484848", 
						alignSelf: "center", marginTop: 3}} >
						EDIT PRIZE DESCRIPTION
					</Button>
				</Stack>

				<Stack>
					<Input 
						disabled
						label="Terms & Conditions"
						labelSx={{
							fontSize: "20px",
							marginBottom: 16
						}}
						sx={{mt: 4}}
						inputSx={{
							'& .MuiInputBase-root': {
								minHeight: '98px',
							},
							"& .MuiOutlinedInput-root": {
								alignItems: "flex-start",
								padding: "12px 14px",           // 👈 Better paragraph feel
							},
							"& .MuiOutlinedInput-input": {
								fontSize: "15px",
								lineHeight: "22px",
								padding: 0,                     // removes default tight padding
							},
							"& .MuiInputBase-root.Mui-disabled": {
								opacity: 1,
								color: "black",
							},
							"& .MuiInputBase-input.Mui-disabled": {
								WebkitTextFillColor: "black", // required for Safari
							},
						}}
						placeholder='Terms and Conditions'
						error={error.tnc}
						value={state.tnc}
						onChange={onDispatch("tnc")} 
					/>
					<Button onClick={() => setContainer("tnc")} sx={{ width: 230, 
						height: 40, borderRadius: 1, 
						color: "white", bgcolor: "#484848", 
						alignSelf: "center", marginTop: 3}} >
						EDIT TNC DESCRIPTION
					</Button>
				</Stack>

				<StyledRow sx={{alignItems: "center"}}>
					<Input 
						label="CTA Text:"
						sx={{mt: 4}}
						inputSx={{
							'& .MuiInputBase-root': {
								width: '258px',
							},
						}}
						placeholder='ENTER NOW'
						error={error["cta.text"]}
						value={state?.cta?.text}
						onChange={onDispatch("cta.text")} />
					<Input 
						label="CTA Link:"
						sx={{mt: 4}}
						inputSx={{
							'& .MuiInputBase-root': {
								width: '258px',
							},
						}}
						placeholder='www.palmsplay.com'
						error={error["cta.link"]}
						value={state?.cta?.link}
						onChange={onDispatch("cta.link")} />
					<Stack sx={{ mt:2.5, width: '258px', gap: 0.5}}>
						<StyledTypography>CTA Preview:</StyledTypography>
						<Button
							sx={{
								background: "linear-gradient(180deg, #DF7B0B 0%, #E5D33D 100%)",
								color: "#000",
								fontWeight: 700,
								textTransform: "none",
								px: 3,
								py: 1.2,
								borderRadius: "7px",
								transition: "0.3s",

								"&:active": {
									transform: "scale(0.98)",
								},
							}}
						>
							{state?.cta?.text || "CTA TEXT"}
						</Button>
					</Stack>
				</StyledRow>
			</Stack>
		</Container>
	)
}